/**
 * StagePicker — clickable stage badge with a fixed-position override picker.
 * Uses position:fixed so it always renders above overflow:hidden containers.
 */

import { useState, useRef, useEffect } from 'react';
import { updateContact } from '../api/backend.js';
import { STAGE_BADGE_CLASS, STAGE_LABELS, PICKABLE_STAGES } from '../utils/stage.js';

const ALL_STAGES = PICKABLE_STAGES;

export default function StagePicker({ contact, stage, getToken, onOverride }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [saving, setSaving] = useState(false);
  const badgeRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function close(e) {
      const insideBadge    = badgeRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideBadge && !insideDropdown) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', () => setOpen(false), { capture: true, once: true });
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  function handleClick() {
    if (saving) return;
    if (!open) {
      const rect = badgeRef.current?.getBoundingClientRect();
      if (rect) setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((v) => !v);
  }

  const hasOverride = !!contact?.stage_override;

  async function pick(newStage) {
    const override = newStage === '__auto__' ? '' : newStage;
    const prevOverride = contact?.stage_override ?? '';
    setSaving(true);
    setOpen(false);
    onOverride?.(contact.contact_id, override);  // optimistic — immediate visual update
    try {
      await updateContact(getToken, contact.contact_id, { stage_override: override });
    } catch (e) {
      console.error('Stage override failed:', e);
      onOverride?.(contact.contact_id, prevOverride);  // rollback
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <span
        ref={badgeRef}
        className={`badge ${STAGE_BADGE_CLASS[stage] ?? 'badge-cold'}`}
        title={hasOverride ? `Override: ${stage}. Click to change.` : 'Click to override stage'}
        onClick={handleClick}
        style={{
          cursor: 'pointer', userSelect: 'none',
          outline: hasOverride ? '2px solid var(--c-primary)' : 'none',
          outlineOffset: 1,
        }}
      >
        {STAGE_LABELS[stage] ?? stage}
        {hasOverride && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.7 }}>✎</span>}
      </span>

      {open && (
        <div ref={dropdownRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
          background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 8, padding: '6px 4px', minWidth: 160,
          boxShadow: '0 4px 20px rgba(0,0,0,.15)',
        }}>
          <div style={{ fontSize: 10, color: 'var(--c-text-3)', padding: '2px 8px 6px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
            Set stage
          </div>
          {ALL_STAGES.map((s) => (
            <button key={s} onClick={() => pick(s)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              width: '100%', textAlign: 'left', padding: '5px 8px',
              background: s === stage ? 'var(--c-bg-2)' : 'transparent',
              border: 'none', cursor: 'pointer', borderRadius: 4, fontSize: 12.5,
            }}>
              <span className={`badge ${STAGE_BADGE_CLASS[s]}`} style={{ pointerEvents: 'none' }}>
                {STAGE_LABELS[s]}
              </span>
              {s === contact?.stage_override && <span style={{ fontSize: 10, color: 'var(--c-text-3)' }}>✎</span>}
              {s !== contact?.stage_override && s === stage && !contact?.stage_override && <span style={{ fontSize: 10, color: 'var(--c-text-3)' }}>auto</span>}
            </button>
          ))}
          {hasOverride && (
            <>
              <div style={{ borderTop: '1px solid var(--c-border)', margin: '4px 0' }} />
              <button onClick={() => pick('__auto__')} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                borderRadius: 4, fontSize: 12, color: 'var(--c-text-3)',
              }}>
                ↺ Reset to auto
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
